/**
 * Table defaults: what a *new* table is born with, and how chairs print.
 *
 * The numbers here are defaults, not a bulk edit. Adding ten 13-chair tables
 * used to mean adding a table, stepping its chairs up three times, and doing it
 * ten more times; setting the default once removes the whole loop.
 *
 * Applying those defaults to the room is now **one** decision instead of five.
 * The old design put an "Apply to 23 / None yet" button on every row, which
 * asked the planner to reason about four independent bulk edits and made the
 * colour block behave differently from the chair block for no reason a user
 * could see. There is now a single opt-in that says exactly what it will do to
 * seated guests, and the save button relabels to match — so nothing is ever
 * applied by a click that looked like it only closed a dialog.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { TABLE_COLORS, screenSwatchFor } from '../lib/colors'
import { defaultSeatsFor } from '../lib/events'
import { MAX_SEATS, MIN_SEATS, maxSeatCount } from '../lib/geometry'
import { shapeName } from '../lib/i18n/shapeName'
import { useT } from '../lib/i18n/I18nProvider'
import type { EventKind, Plan, SeatLabelMode, TableColor, TableShape } from '../lib/types'
import { guestsUnseatedByDefault, usePlanStore } from '../store/planStore'

interface Props {
  open: boolean
  onClose: () => void
  plan: Plan
}

/**
 * Every shape has a stepper. The sweetheart shape used to be fixed at two, the
 * couple; as a head table at a company dinner it seats the hosts as well.
 */
const STEPPER_SHAPES: TableShape[] = ['round', 'square', 'rectangle', 'sweetheart']

export function TableSettingsDialog({ open, onClose, plan }: Props) {
  const t = useT()
  const setDefaultSeatCount = usePlanStore((s) => s.setDefaultSeatCount)
  const setDefaultTableColor = usePlanStore((s) => s.setDefaultTableColor)
  const setSeatLabels = usePlanStore((s) => s.setSeatLabels)
  const applyDefaultSeatCount = usePlanStore((s) => s.applyDefaultSeatCount)
  const applyDefaultTableColor = usePlanStore((s) => s.applyDefaultTableColor)
  const transaction = usePlanStore((s) => s.transaction)
  const showToast = usePlanStore((s) => s.showToast)

  const [applyExisting, setApplyExisting] = useState(false)

  // Off again every time the dialog is opened. A destructive opt-in that
  // remembers being ticked is a trap the second time round.
  useEffect(() => {
    if (open) setApplyExisting(false)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const tableCount = plan.tables.length

  const save = () => {
    if (!applyExisting) {
      onClose()
      return
    }

    const atRisk = STEPPER_SHAPES.reduce(
      (sum, shape) => sum + guestsUnseatedByDefault(plan, shape),
      0,
    )
    if (atRisk > 0 && !confirm(t.tableSettings.confirmUnseat(atRisk))) return

    // One undo step for the whole apply: the shapes and the colour are a single
    // decision here, so taking it back must be a single ⌘Z.
    let tables = 0
    let unseated = 0
    transaction(() => {
      for (const shape of STEPPER_SHAPES) {
        const result = applyDefaultSeatCount(shape)
        tables += result.tables
        unseated += result.unseated
      }
      applyDefaultTableColor()
    })

    showToast(
      unseated > 0
        ? t.tableSettings.appliedWithUnseated(tables, unseated)
        : t.tableSettings.applied(tables),
      unseated > 0 ? 'warn' : 'success',
    )
    onClose()
  }

  const resetDefaults = () => {
    // One entry for the whole reset, same as the apply above.
    transaction(() => {
      // The kind's own counts: a corporate plan's head table starts at six.
      const defaults = defaultSeatsFor(plan.eventKind)
      for (const shape of STEPPER_SHAPES) setDefaultSeatCount(shape, defaults[shape])
      setDefaultTableColor('blue')
      setSeatLabels('name')
    })
    showToast(t.tableSettings.defaultsReset, 'info')
  }

  const labelModes: SeatLabelMode[] = ['name', 'initials', 'none']

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            className="modal modal--settings"
            /* A modal is not anchored to a trigger, so it scales from its own
               centre — and from 0.96, never from nothing. */
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 6 }}
            transition={{ type: 'spring', duration: 0.34, bounce: 0.12 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="table-settings-title"
          >
            <header className="modal-head">
              <div>
                <h2 id="table-settings-title" className="modal-title">
                  {t.tableSettings.title}
                </h2>
                <p className="modal-lede">{t.tableSettings.lede}</p>
              </div>
              <button
                type="button"
                className="btn btn-chrome btn-icon"
                onClick={onClose}
                aria-label={t.common.close}
              >
                ✕
              </button>
            </header>

            <div className="modal-body">
              <section className="settings-block">
                <span className="eyebrow">{t.tableSettings.newTableChairs}</span>
                <ul className="settings-rows">
                  {STEPPER_SHAPES.map((shape) => (
                    <SeatDefaultRow
                      key={shape}
                      shape={shape}
                      kind={plan.eventKind}
                      value={plan.settings.defaultSeats[shape]}
                      onChange={(next) => setDefaultSeatCount(shape, next)}
                    />
                  ))}
                </ul>
                <p className="inspector-hint">{t.tableSettings.chairsHint(MAX_SEATS)}</p>
              </section>

              <section className="settings-block">
                <span className="eyebrow">{t.tableSettings.newTableColour}</span>
                <div className="swatch-row">
                  {TABLE_COLORS.map((color) => (
                    <ColourChoice
                      key={color}
                      color={color}
                      active={plan.settings.defaultTableColor === color}
                      onSelect={() => setDefaultTableColor(color)}
                    />
                  ))}
                </div>
              </section>

              <section className="settings-block">
                <span className="eyebrow">{t.tableSettings.chairsInPdf}</span>
                <div className="segmented segmented--wide">
                  {labelModes.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`segmented-item${
                        plan.settings.seatLabels === mode ? ' is-active' : ''
                      }`}
                      onClick={() => setSeatLabels(mode)}
                      aria-pressed={plan.settings.seatLabels === mode}
                    >
                      {t.tableSettings.seatLabel[mode]}
                    </button>
                  ))}
                </div>
                <p className="inspector-hint">
                  {t.tableSettings.seatLabelHint[plan.settings.seatLabels]}
                </p>
              </section>

              {tableCount > 0 && (
                <label className={`apply-box${applyExisting ? ' is-armed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={applyExisting}
                    onChange={(event) => setApplyExisting(event.target.checked)}
                  />
                  <span>
                    <strong>{t.tableSettings.applyExisting(tableCount)}</strong>
                    <small>
                      {applyExisting
                        ? t.tableSettings.applyExistingOn
                        : t.tableSettings.applyExistingOff}
                    </small>
                  </span>
                </label>
              )}
            </div>

            <footer className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={resetDefaults}>
                {t.tableSettings.resetDefaults}
              </button>
              <button type="button" className="btn btn-outline modal-foot-end" onClick={onClose}>
                {t.common.cancel}
              </button>
              <button type="button" className="btn btn-primary" onClick={save}>
                {applyExisting
                  ? t.tableSettings.saveAndApply(tableCount)
                  : t.tableSettings.saveDefaults}
              </button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function SeatDefaultRow({
  shape,
  kind,
  value,
  onChange,
}: {
  shape: TableShape
  kind: EventKind
  value: number
  onChange: (next: number) => void
}) {
  const t = useT()
  const ceiling = maxSeatCount(shape)

  return (
    <li className="settings-row">
      <span className="settings-row-name">
        <span className={`shape-chip shape-chip--${shape} shape-chip--sm`} />
        {shapeName(t, shape, kind)}
      </span>
      <div className="stepper stepper--inline">
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onChange(value - 1)}
          disabled={value <= MIN_SEATS}
          aria-label={t.inspector.removeChair}
        >
          −
        </button>
        <span className="mono stepper-readout">{value}</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onChange(value + 1)}
          disabled={value >= ceiling}
          aria-label={t.inspector.addChair}
          title={value >= ceiling ? t.inspector.atMaxChairs(ceiling) : t.inspector.addChair}
        >
          +
        </button>
      </div>
    </li>
  )
}

function ColourChoice({
  color,
  active,
  onSelect,
}: {
  color: TableColor
  active: boolean
  onSelect: () => void
}) {
  const swatch = screenSwatchFor(color)
  return (
    <button
      type="button"
      className={`swatch${active ? ' is-active' : ''}`}
      onClick={onSelect}
      aria-pressed={active}
      aria-label={swatch.label}
      title={swatch.label}
    >
      <span className="swatch-dot" style={{ background: swatch.surface, borderColor: swatch.edge }}>
        <span className="swatch-seat" style={{ background: swatch.accent }} />
      </span>
      <span className="swatch-name">{swatch.label}</span>
    </button>
  )
}
